import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';

/**
 * Landingspagina van de link uit de bevestigingsmail.
 * De token staat in de query: /verifieer-email?token=...
 */
export const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState(token ? 'bezig' : 'geen-token');
  const [melding, setMelding] = useState('');
  const [email, setEmail] = useState('');
  const [versturen, setVersturen] = useState(false);
  // In ontwikkelmodus draait een effect twee keer; zonder deze vlag zou de
  // tweede aanroep falen omdat de token dan al verbruikt is.
  const gedaan = useRef(false);

  useEffect(() => {
    if (!token || gedaan.current) return;
    gedaan.current = true;
    (async () => {
      try {
        const { data } = await authAPI.verifyEmail(token);
        setMelding(data.message || 'Je e-mailadres is bevestigd.');
        setStatus('gelukt');
      } catch (e) {
        setMelding(e.response?.data?.detail
          || 'Deze verificatielink is niet meer geldig.');
        setStatus('mislukt');
      }
    })();
  }, [token]);

  const opnieuw = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setVersturen(true);
    try {
      const { data } = await authAPI.resendVerification(email.trim());
      toast.success(data.message || 'Als dit adres bekend is, is er een nieuwe mail onderweg.');
    } catch (err) {
      toast.error('Versturen mislukt. Probeer het later opnieuw.');
    } finally {
      setVersturen(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center"
           data-testid="verify-email-page">
        {status === 'bezig' && (
          <>
            <Loader2 className="w-12 h-12 text-[#1A4D2E] mx-auto mb-4 animate-spin" />
            <h1 className="text-xl font-bold text-gray-900">Bezig met bevestigen…</h1>
          </>
        )}

        {status === 'gelukt' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900">E-mailadres bevestigd</h1>
            <p className="text-gray-600 mt-2">{melding}</p>
            <Link to="/dashboard">
              <Button className="btn-primary mt-6 w-full h-12">Naar mijn dashboard</Button>
            </Link>
          </>
        )}

        {(status === 'mislukt' || status === 'geen-token') && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900">
              {status === 'geen-token' ? 'Geen verificatielink' : 'Link niet meer geldig'}
            </h1>
            <p className="text-gray-600 mt-2">
              {status === 'geen-token'
                ? 'Open de link uit de bevestigingsmail die we je gestuurd hebben.'
                : melding}
            </p>

            <form onSubmit={opnieuw} className="mt-6 space-y-3 text-left">
              <label className="text-sm text-gray-700 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Stuur een nieuwe bevestigingsmail
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jouw@restaurant.be"
                className="h-12"
                data-testid="resend-email-input"
              />
              <Button type="submit" disabled={versturen} className="btn-primary w-full h-12">
                {versturen ? 'Versturen…' : 'Verstuur opnieuw'}
              </Button>
            </form>

            <Link to="/login" className="block mt-4 text-sm text-[#1A4D2E] hover:underline">
              Terug naar inloggen
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
